import React, { useState, useEffect } from 'react';
import { api } from '../api';
import { nfConfirm } from './confirm';
import { useDeliverySlots } from '../deliverySlots';

// Шаги по 15 минут внутри слота «09:00-11:00» → ['09:00', '09:15', …, '11:00']
function slotSteps(slot) {
  const m = /^(\d{2}):(\d{2})-(\d{2}):(\d{2})$/.exec(slot || '');
  if (!m) return [];
  const a = (+m[1]) * 60 + (+m[2]);
  const b = (+m[3]) * 60 + (+m[4]);
  const out = [];
  for (let t = a; t <= b; t += 15) out.push(`${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`);
  return out;
}

// Коды статусов Flowwow (enum их API + наши отрицательные для терминальных случаев),
// синхронно с backend/app/services/flowwow_orders.py FW_STATUS_LABELS.
export const FW_STATUS_LABELS = {
  1: 'Новый', 2: 'Принят', 5: 'Фото до доставки', 4: 'Передан в доставку', 3: 'Выполнен', 7: 'Ждёт самовывоза',
  10: 'Собран (почта)', 11: 'Доставляется почтой', 12: 'Ждёт в отделении',
  '-1': 'Отклонён магазином', '-2': 'Отменён клиентом',
};

// deliveryType Flowwow: 4 — самовывоз покупателем (закрываем мы при выдаче),
// 5 — курьер Flowwow (закрывает курьер в своём приложении). Своя доставка — наш
// delivery_type 'delivery': везёт наш курьер, «У курьера» и «Завершён» уходят на площадку.
const DELIVERY_PICKUP = 4;
const DELIVERY_FLOWWOW_COURIER = 5;
// «Силами магазина» (и 0 — способ не выбран): у нас доставка, если включена своя доставка
const OWN_DELIVERY_TYPES = [0, 1, 2, 10, 11, 12, 20, 21, 22, 24];

// Имя заказчика Flowwow. Когда букет другому человеку, площадка присылает только его телефон
// (имя — у получателя), а бэк пишет заглушку «Покупатель Flowwow» (flowwow_orders.py) —
// тогда просто «Клиент»: рядом и так розовый бейдж Flowwow
const FW_NO_NAME = 'Покупатель Flowwow';
export function flowwowCustomerName(order) {
  const name = (order?.customer_name || '').trim();
  return name && name !== FW_NO_NAME ? name : 'Клиент';
}

export function flowwowDeliveryLabel(order) {
  if (order?.delivery_type === 'delivery') {
    return order?.external_delivery_type === DELIVERY_FLOWWOW_COURIER ? 'Везём сами (самодоставка)' : 'Своя доставка';
  }
  if (order?.external_delivery_type === DELIVERY_PICKUP) return 'Самовывоз покупателем';
  if (order?.external_delivery_type === DELIVERY_FLOWWOW_COURIER) return 'Курьер Flowwow';
  return 'Заберут из магазина';
}

/**
 * Блок канала Flowwow под общей линейкой статусов: только действия и предупреждения
 * площадки — запасная «Принять» (если автопринятие не прошло), «Везём сами», адрес,
 * время вручения, новое фото. Статус заказа меняется общей линейкой карточки, площадку
 * обновляет сервер (services/flowwow_channel.py): «Собран» — фото покупателю, «У курьера»,
 * «Завершён», «Отменён» — отказ. Какие шаги есть у заказа, решает канал (status_steps).
 */
function FlowwowOrderPanel({ order, showToast, onUpdated }) {
  const [busy, setBusy] = useState(null);
  const [fwSettings, setFwSettings] = useState(null);
  const [addrOpen, setAddrOpen] = useState(false);
  const [addr, setAddr] = useState('');
  // Адрес Flowwow без населённого пункта нашёлся в нескольких местах — варианты кнопками
  const [addrVariants, setAddrVariants] = useState(null);
  const { timeSlots, shopTimezone } = useDeliverySlots();
  const [winOpen, setWinOpen] = useState(false);
  const [win, setWin] = useState({ date: '', slot: '', from: '', to: '' });

  const ext = order.external_status;
  const isOwn = order.delivery_type === 'delivery';
  const isFlowwowCourier = !isOwn && order.external_delivery_type === DELIVERY_FLOWWOW_COURIER;
  // Своя доставка, а заказ пришёл самовывозом (до включения настройки) — тоже можно перевести
  const isOwnTypeAsPickup = !isOwn && OWN_DELIVERY_TYPES.includes(order.external_delivery_type);
  // Своя доставка без координат или цены: адрес не нашёлся / дальше тарифа — курьеру 0 ₽
  const deliveryProblem = isOwn && (order.delivery_lat == null || !(order.delivery_cost > 0));
  const noAddress = isOwn && !(order.delivery_address || '').trim();
  const isNew = ext === 1 && order.status !== 'cancelled';
  const terminal = order.status === 'cancelled' || [3, -1, -2].includes(ext);

  const needSettings = isFlowwowCourier || isOwnTypeAsPickup;
  useEffect(() => {
    if (!needSettings) return;
    let alive = true;
    // Настройки читаем при каждом открытии: их могли переключить в этом же сеансе
    api.getFlowwowSettings().then(s => { if (alive) setFwSettings(s); }).catch(() => {});
    return () => { alive = false; };
  }, [needSettings]);

  const run = async (kind, fn, okText) => {
    setBusy(kind);
    try {
      await fn();
      if (okText) showToast(okText);
      if (onUpdated) await onUpdated();
    } catch (error) {
      showToast(error.message || 'Ошибка Flowwow', 'error');
      if (onUpdated) await onUpdated();
    } finally {
      setBusy(null);
    }
  };

  const photos = order.photos || [];
  // Пересъёмка: есть фото новее отправленного — можно отправить покупателю ещё раз
  const latest = photos[photos.length - 1];
  const canResend = !terminal && order.status === 'ready' && latest && !latest.sent_at;
  // Курьер Flowwow ещё не забрал заказ (у нас не «У курьера», на площадке не «Передан в доставку»)
  const canSelfDeliver = ((isFlowwowCourier && !!fwSettings?.self_delivery) || (isOwnTypeAsPickup && !!fwSettings?.delivery_own))
    && !terminal && ![1, 4, 7].includes(ext) && !['courier', 'completed', 'cancelled'].includes(order.status);
  // Адрес — до выдачи курьеру: потом «Передан в доставку» уже на площадке и курьер в пути
  const canEditAddress = isOwn && !terminal && !['courier', 'completed', 'cancelled'].includes(order.status);
  useEffect(() => {
    if (!(canEditAddress && deliveryProblem)) { setAddrVariants(null); return undefined; }
    let alive = true;
    api.getFlowwowAddressVariants(order.id).then(r => { if (alive) setAddrVariants(r); }).catch(() => {});
    return () => { alive = false; };
  }, [order.id, canEditAddress, deliveryProblem, order.delivery_address]);
  // «Согласовать с получателем» у своей доставки: время вручения вносим у нас (на Flowwow не уходит)
  const agreeWindow = isOwn && order.external_time_type === 0 && !terminal && !['courier', 'completed', 'cancelled'].includes(order.status);
  const windowAgreed = agreeWindow && !!order.delivery_time;
  // «Уточнить у получателя»: адрес и время уточняют у получателя и вносят на Flowwow, сверка
  // подтягивает их сюда — у нас ничего не вводим, одна спокойная строка (владелец 25.10)
  const waitAddress = canEditAddress && noAddress;
  const waitTime = agreeWindow && !windowAgreed;
  const waitText = waitAddress && waitTime ? 'Адрес и время доставки уточняются у получателя и вносятся на Flowwow'
    : waitAddress ? 'Адрес доставки уточняется у получателя и вносится на Flowwow'
    : waitTime ? 'Время доставки уточняется у получателя и вносится на Flowwow' : null;
  const hasPanel = isNew || canSelfDeliver || (agreeWindow && (windowAgreed || winOpen))
    || (canEditAddress && ((deliveryProblem && !noAddress) || addrOpen))
    || !!order.refuse_state || canResend;
  const openWindowForm = () => {
    // «сегодня» — в поясе магазина (toISOString — UTC: ночью дал бы вчера)
    const today = new Date().toLocaleDateString('en-CA', { timeZone: shopTimezone || 'Europe/Moscow' });
    // слот заказа не из текущей сетки — первый слот сетки (то, что видно в списке, то и уходит)
    const slot = (timeSlots || []).includes(order.delivery_time) ? order.delivery_time : ((timeSlots || [])[0] || '');
    const inSlot = slot === order.delivery_time;
    setWin({ date: order.delivery_date || today, slot, from: inSlot ? (order.deliver_from || '') : '', to: inSlot ? (order.deliver_to || '') : '' });
    setWinOpen(true);
  };
  const saveWindow = async () => {
    if (!win.date || !win.slot) { showToast('Выберите дату и слот', 'error'); return; }
    setBusy('window');
    try {
      await api.flowwowSetWindow(order.id, { delivery_date: win.date, slot: win.slot, deliver_from: win.from || null, deliver_to: win.to || null });
      showToast('Время вручения сохранено — заказ в слоте и в маршрутах');
      setWinOpen(false);
      if (onUpdated) await onUpdated();
    } catch (error) {
      showToast(error.message || 'Ошибка', 'error');
    } finally {
      setBusy(null);
    }
  };

  const ownDelivery = async (address) => {
    setBusy('own');
    try {
      const res = await api.flowwowOwnDelivery(order.id, address);
      if (res?.own_delivery_problem) showToast(`Доставка не посчитана: ${res.own_delivery_problem}`, 'error');
      else showToast(isOwn ? 'Доставка пересчитана по нашему тарифу' : 'Везём сами: заказ в доставке, курьера назначьте как обычно');
      setAddrOpen(false);
      if (onUpdated) await onUpdated();
    } catch (error) {
      showToast(error.message || 'Ошибка', 'error');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {waitText && (
        <div style={{ padding: '8px 12px', background: '#EBEDEC', '--ch': '8px', color: '#474D49', fontSize: 13 }}>
          {waitText}
        </div>
      )}
      {/* «Уточнить адрес у получателя»: площадка просит заказчика доплатить доставку, когда адрес
          внесут, но саму доплату в API не показывает (в данных 0 ₽ и после оплаты — #159, 12.10).
          Поэтому спокойное напоминание, а не «не оплачена» */}
      {order.delivery_unpaid && (
        <div style={{ padding: '8px 12px', background: '#EBEDEC', '--ch': '8px', color: '#474D49', fontSize: 13 }}>
          Доставка без адреса: доплату за доставку Flowwow в данных не показывает. При сомнении проверьте в Flowwow Seller
        </div>
      )}
      {/* Плашка Flowwow — только когда есть что сделать или о чём предупредить. Номер площадки —
          в заголовке карточки, бейдж — у имени клиента, статус — наша линейка выше; тип доставки
          и «срочно» — в блоке «Доставка». Заказ принимается автоматически: «Принять» — если
          автопринятие не прошло */}
      {hasPanel && (
      <div style={{ padding: '10px 12px', background: '#FCE4EC', '--ch': '8px', borderLeft: '3px solid #C2185B', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {(isNew || canSelfDeliver) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {isNew && (
            <button className="btn btn-primary btn-sm" disabled={!!busy}
              onClick={() => run('accept', () => api.acceptFlowwowOrder(order.id), 'Заказ принят на Flowwow')}>
              {busy === 'accept' ? 'Принимаем...' : 'Принять на Flowwow'}
            </button>
          )}
          {canSelfDeliver && (
            <button type="button" className="btn btn-secondary btn-sm" disabled={!!busy}
              title="Flowwow не нашёл курьера и предложил самодоставку, вы приняли её в приложении Flowwow Seller. Заказ станет нашей доставкой: адрес, тариф, курьер — как обычно"
              onClick={async () => {
                const text = isFlowwowCourier
                  ? 'Везём сами? Сначала примите самодоставку в приложении Flowwow Seller. Заказ станет нашей доставкой: доставка по нашему тарифу, курьера назначите как обычно, «У курьера» и «Завершён» уйдут на Flowwow.'
                  : 'Покупатель выбрал доставку магазина, а заказ пришёл самовывозом (своя доставка тогда была выключена). Перевести в нашу доставку: адрес с Flowwow, доставка по нашему тарифу, курьера назначите как обычно?';
                if (!(await nfConfirm(text, { confirmText: 'Везём сами' }))) return;
                ownDelivery(null);
              }}>
              {busy === 'own' ? 'Считаем доставку...' : 'Везём сами'}
            </button>
          )}
        </div>
        )}

        {agreeWindow && (windowAgreed || winOpen) && (
          <div style={{ padding: '8px 10px', background: windowAgreed && !winOpen ? '#fff' : '#F9EBCF', '--ch': '6px' }}>
            {windowAgreed && !winOpen ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: 13, color: '#1C3D2E' }}>
                <span>
                  Время вручения согласовано: <b>{(order.delivery_date || '').split('-').reverse().join('.')} · {order.deliver_from && order.deliver_to ? `${order.deliver_from}–${order.deliver_to}` : order.delivery_time}</b>
                </span>
                <button type="button" onClick={openWindowForm}
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#5E6561', fontSize: 12, textDecoration: 'underline' }}>
                  Изменить
                </button>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: 12, color: '#5E6561', marginBottom: 6 }}>
                  Время вручения — у нас: по нему слот и маршрут курьера. Покупателю на Flowwow не уходит
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                  <input type="date" className="form-input" aria-label="Дата вручения" value={win.date}
                    onChange={(e) => setWin(w => ({ ...w, date: e.target.value }))} style={{ fontSize: 13, padding: '6px 8px', width: 150 }} />
                  <select className="form-input" aria-label="Слот" value={win.slot}
                    onChange={(e) => setWin(w => ({ ...w, slot: e.target.value, from: '', to: '' }))} style={{ fontSize: 13, padding: '6px 8px', width: 140 }}>
                    {(timeSlots || []).map(s => <option key={s} value={s}>{s.replace('-', '–')}</option>)}
                  </select>
                  <span style={{ fontSize: 12, color: '#5E6561' }}>окно</span>
                  <select className="form-input" aria-label="Окно с" value={win.from}
                    onChange={(e) => { const v = e.target.value; setWin(w => ({ ...w, from: v, to: w.to && v && w.to <= v ? '' : w.to })); }} style={{ fontSize: 13, padding: '6px 8px', width: 90 }}>
                    <option value="">с —</option>
                    {slotSteps(win.slot).map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <select className="form-input" aria-label="Окно до" value={win.to}
                    onChange={(e) => setWin(w => ({ ...w, to: e.target.value }))} style={{ fontSize: 13, padding: '6px 8px', width: 90 }}>
                    <option value="">до —</option>
                    {slotSteps(win.slot).filter(t => !win.from || t > win.from).map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <button type="button" className="btn btn-primary btn-sm" disabled={!!busy} onClick={saveWindow}>
                    {busy === 'window' ? 'Сохраняем...' : 'Сохранить'}
                  </button>
                  <button type="button" className="btn btn-secondary btn-sm" disabled={!!busy} onClick={() => setWinOpen(false)}>Отмена</button>
                </div>
              </div>
            )}
          </div>
        )}

        {canEditAddress && ((deliveryProblem && !noAddress) || addrOpen) && (
          <div style={{ padding: '8px 10px', background: deliveryProblem && !noAddress ? '#FFE6E3' : '#fff', '--ch': '6px' }}>
            {deliveryProblem && !noAddress && (addrVariants?.variants?.length > 0 ? (
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#CB4D45', marginBottom: 6 }}>
                  Адрес «{addrVariants.address}» есть в нескольких местах — выберите нужный по Flowwow Seller
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {addrVariants.variants.map(v => (
                    <button key={v.value} type="button" className="btn btn-secondary btn-sm" disabled={!!busy}
                      title={v.value} onClick={() => ownDelivery(v.value)}>
                      {v.value}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{ fontSize: 12, fontWeight: 600, color: '#CB4D45', marginBottom: 6 }}>
                Доставка не посчитана — курьеру 0 ₽. Уточните адрес (улица, дом) и пересчитайте по нашему тарифу
              </div>
            ))}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <input className="form-input" style={{ flex: 1, minWidth: 180, fontSize: 13, padding: '6px 10px' }}
                aria-label="Адрес доставки: улица, дом" value={addr} placeholder={order.delivery_address || 'Улица, дом'}
                onChange={(e) => setAddr(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && addr.trim() && !busy) ownDelivery(addr.trim()); }} />
              <button type="button" className="btn btn-primary btn-sm" disabled={!!busy || !addr.trim()}
                onClick={() => ownDelivery(addr.trim())}>
                {busy === 'own' ? 'Считаем...' : 'Пересчитать доставку'}
              </button>
              {addrOpen && !deliveryProblem && (
                <button type="button" className="btn btn-secondary btn-sm" disabled={!!busy} onClick={() => setAddrOpen(false)}>Отмена</button>
              )}
            </div>
          </div>
        )}

        {order.refuse_state && (
          <div style={{ padding: '6px 10px', background: '#FFE6E3', '--ch': '6px', color: '#CB4D45', fontSize: 12, fontWeight: 600 }}>
            {order.refuse_state === 'retry'
              ? '⚠️ Отказ на Flowwow пока не отправлен — повторяем автоматически. Если через полчаса заказ на площадке всё ещё активен, отмените его в приложении Flowwow Seller.'
              : '❌ Отказ на Flowwow не отправлен. Отмените заказ в приложении Flowwow Seller, иначе площадка оштрафует за автоотмену.'}
          </div>
        )}

        {/* Когда фото ушло — видно по «Собран» в истории статусов и розовой рамке в «Фото сборки»;
            здесь только пересъёмка: есть фото новее отправленного */}
        {canResend && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 12, color: '#5E6561' }}>Есть новое фото — после согласования отправьте его покупателю</div>
            <button className="btn btn-primary btn-sm" disabled={!!busy}
              onClick={() => run('resend', () => api.flowwowResendPhoto(order.id), 'Новое фото отправлено покупателю')}>
              {busy === 'resend' ? 'Отправка...' : 'Отправить клиенту новое фото'}
            </button>
          </div>
        )}
      </div>
      )}
      {/* Адрес «нашёлся», но не тот (#247, 24.10: DaData взяла Порошино вместо Малой Субботихи) —
          поправить можно до выдачи курьеру, как при ненайденном */}
      {canEditAddress && !deliveryProblem && !addrOpen && (
        <div>
          <button type="button" onClick={() => { setAddr(order.delivery_address || ''); setAddrOpen(true); }}
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#5E6561', fontSize: 12, textDecoration: 'underline' }}>
            Изменить адрес и пересчитать доставку
          </button>
        </div>
      )}
    </div>
  );
}

export default FlowwowOrderPanel;
